import { createSignal, For, onCleanup, onMount, Show } from 'solid-js';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
}

export default function Dropdown(props: DropdownProps) {
  const [open, setOpen] = createSignal(false);
  let wrapRef!: HTMLDivElement;

  const selectedLabel = () =>
    props.options.find((o) => o.value === props.value)?.label ?? props.value;

  function toggle() {
    setOpen((prev) => !prev);
  }

  function pick(value: string) {
    props.onChange(value);
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') setOpen(false);
    if (e.key === 'Enter') toggle();
  }

  function onClickOutside(e: MouseEvent) {
    if (wrapRef && !wrapRef.contains(e.target as Node)) {
      setOpen(false);
    }
  }

  onMount(() => document.addEventListener('click', onClickOutside));
  onCleanup(() => document.removeEventListener('click', onClickOutside));

  return (
    <div ref={wrapRef} class={`ch-dropdown${open() ? ' open' : ''}`}>
      <button
        type="button"
        class={`ch-dropdown-trigger${open() ? ' open' : ''}`}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        {selectedLabel()}
      </button>
      <span class="ch-dropdown-arrow">
        <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
      <Show when={open()}>
        <div class="ch-dropdown-menu">
          <For each={props.options}>
            {(opt) => (
              <button
                type="button"
                class={`ch-dropdown-option${props.value === opt.value ? ' selected' : ''}`}
                onClick={() => pick(opt.value)}
              >
                <span>{opt.label}</span>
                <svg class="ch-dropdown-check" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
